import React, { useState, useEffect, useCallback } from "react";
import { useTranslation } from "react-i18next";
import { Form, Formik } from "formik";
import * as Yup from "yup";
import { toast } from "react-hot-toast";
import { Grid, Tab, Tabs, Box, Button } from "@mui/material";
import { FormikSaveBar } from "../../utils/FormikSaveBar";
import { PMField } from "../../utils/PMField";
import { useDispatch, useSelector } from "react-redux";
import PMSwitch from "../../utils/PMSwitch";
import { createOfferAsync, updateOfferAsync, getOfferAsync } from "../../../slices/offer";
import { mapErrors } from "../../Utils";
import { ResourceLoading } from "../../utils/ResourceLoading";
import { ProjectSelector } from "../../utils/moduleSelectors/projectSelector";
import { CustomerSelector } from "../../utils/moduleSelectors/customerSelector";
import { ContactsSelector } from "../../utils/moduleSelectors/contactSelector";
import { EmployeeSelector } from "../../utils/moduleSelectors/employeeSelector";
import { DepartmentSelector } from "../../utils/moduleSelectors/departmentSelector";
import OfferItems from "./offer-items";
import OfferNotes from "./offer-notes";
import OfferHistory from "./offer-history";
import OfferCoverPage from "./offer-cover-page";
import { OfferSendDialog } from "./offer-send-dialog";
import { 
  DocumentTextIcon, 
  DocumentIcon, 
  ClockIcon,
  PrinterIcon,
  PaperAirplaneIcon,
  PhotoIcon,
  ClipboardDocumentIcon
} from "@heroicons/react/24/outline";
import { useSettings } from "../../../contexts/settings-context";
import axios from "../../../api/axios";
import { PMDialog } from "../../utils/PMDialog";

function TabPanel({ children, value, index, ...other }) {
  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`offer-tabpanel-${index}`}
      aria-labelledby={`offer-tab-${index}`}
      {...other}
    >
      {value === index && <Box sx={{ py: 3 }}>{children}</Box>}
    </div>
  );
}

export const OfferInfo = (props) => {
  const { t } = useTranslation();
  const { offer, isLoading } = props;
  const dispatch = useDispatch();
  const { priceFormatter } = useSettings();
  const [activeTab, setActiveTab] = useState(0);
  const [sendDialogOpen, setSendDialogOpen] = useState(false);
  const [summaryDialogOpen, setSummaryDialogOpen] = useState(false);
  const [summaryDialogType, setSummaryDialogType] = useState("overview");
  const [titleFieldOpen, setTitleFieldOpen] = useState(Boolean(offer?.title));
  const [offerMetaFieldsOpen, setOfferMetaFieldsOpen] = useState(false);
  const [isAutoSaving, setIsAutoSaving] = useState(false);
  const autoSaveTimeoutRef = React.useRef(null);
  const formikRef = React.useRef(null);
  const lastSavedRef = React.useRef(null);
  const isInitializingRef = React.useRef(true);

  const updateOrCreateOffer = useCallback(async (values) => {
    if (values.id) {
      return dispatch(updateOfferAsync(values));
    } else {
      return dispatch(createOfferAsync(values));
    }
  }, [dispatch]);

  // Auto-save function
  const autoSave = useCallback(async (values) => {
    if (!values.id) return; // Don't auto-save new offers
    if (isInitializingRef.current) return; // Don't auto-save during initialization
    
    // Create a hash of the values to compare
    const valuesHash = JSON.stringify({
      items: values.items,
      calculations: values.calculations,
      notes: values.notes,
      customer_id: values.customer_id,
      project_id: values.project_id,
      offer_date: values.offer_date,
      delivery_date: values.delivery_date,
      reference: values.reference,
      comment: values.comment,
      active: values.active,
    });
    
    // Skip if nothing changed
    if (lastSavedRef.current === valuesHash) {
      return;
    }
    
    // Clear existing timeout
    if (autoSaveTimeoutRef.current) {
      clearTimeout(autoSaveTimeoutRef.current);
    }

    // Set new timeout for auto-save (debounce 2 seconds)
    autoSaveTimeoutRef.current = setTimeout(async () => {
      setIsAutoSaving(true);
      try {
        await updateOrCreateOffer(values);
        // Update last saved hash after successful save
        lastSavedRef.current = valuesHash;
        // Silent save - no toast notification
      } catch (err) {
        console.error("Auto-save failed:", err);
      } finally {
        setIsAutoSaving(false);
      }
    }, 2000);
  }, [updateOrCreateOffer]);

  // Mark initialization as complete after first render
  useEffect(() => {
    if (offer?.id) {
      // Small delay to ensure formik is initialized
      const timer = setTimeout(() => {
        isInitializingRef.current = false;
        // Set initial hash after initialization
        if (formikRef.current) {
          const values = formikRef.current.values;
          lastSavedRef.current = JSON.stringify({
            items: values.items,
            calculations: values.calculations,
            notes: values.notes,
            customer_id: values.customer_id,
            project_id: values.project_id,
            offer_date: values.offer_date,
            delivery_date: values.delivery_date,
            reference: values.reference,
            comment: values.comment,
            active: values.active,
          });
        }
      }, 500);
      return () => clearTimeout(timer);
    }
  }, [offer?.id]);

  // Auto-save is disabled to prevent circular updates and form resets
  // Users can manually save using the save button

  const currentDate = new Date().toISOString().slice(0, 10);
  const [optionalOrderNumber, setOptionalOrderNumber] = useState(false);
  const [selectedDepartment, setSelectedDepartment] = useState(
    offer?.department?.id ? offer?.department : null
  );

  const handleProjectChange = (data, formik) => {
    const { setFieldValue, values } = formik;
    if (data?.department?.id && !values.department_id) {
      setFieldValue("department_id", data?.department?.id);
      setSelectedDepartment(data?.department);
    }
  };

  const currentUser = useSelector((state) => state.session.currentUser);

  const openSummaryDialog = (type = "overview") => {
    setSummaryDialogType(type);
    setSummaryDialogOpen(true);
  };

  const copyValue = async (label, value) => {
    if (!value) {
      toast.error(`${label} mangler`);
      return;
    }

    try {
      await navigator.clipboard.writeText(String(value));
      toast.success(`${label} kopiert`);
    } catch (err) {
      console.error("Could not copy value:", err);
      toast.error("Kunne ikke kopiere");
    }
  };

  // Helper function to extract hours from a calculation
  const extractHoursFromCalculation = (calculation) => {
    let hours = 0;
    let hourlySales = 0;
    let hourlyCost = 0;

    if (calculation && calculation.items && Array.isArray(calculation.items)) {
      calculation.items.forEach((item) => {
        const itemHours = parseFloat(item.hours || 0);
        const hourRate = parseFloat(item.hour_rate || 0);
        const costPrice = parseFloat(item.purchase_price || 0);
        const quantity = parseFloat(item.quantity || 1);

        if (itemHours > 0) {
          hours += itemHours * quantity;
          if (hourRate > 0) {
            hourlySales += itemHours * hourRate * quantity;
          }
          // Estimate hourly cost from purchase price if available
          if (costPrice > 0) {
            hourlyCost += itemHours * costPrice * quantity;
          }
        }

        // Also check nested calculations recursively
        if (item.nested_calculation) {
          const nestedHours = extractHoursFromCalculation(item.nested_calculation);
          hours += nestedHours.hours;
          hourlySales += nestedHours.hourlySales;
          hourlyCost += nestedHours.hourlyCost;
        }
      });
    }

    return { hours, hourlySales, hourlyCost };
  };

  // Helper function to parse formatted number back to number
  const parseFormattedNumber = (value) => {
    if (!value || value === "") return 0;
    const cleaned = String(value).replace(/\s/g, "").replace(",", ".");
    return parseFloat(cleaned) || 0;
  };

  // Calculate margins
  const calculateMargins = (items, calculations) => {
    let totalSales = 0;
    let totalCost = 0;
    let totalHours = 0;
    let totalHourlyCost = 0;
    let totalHourlySales = 0;

    // Calculate from items
    if (items && items.length > 0) {
      items.forEach((item) => {
        if (!item.group_row) {
          // Handle calculation type items (kalkyler lagt til som linjer)
          if (item.type === "calculation" && item.calculation) {
            const calc = item.calculation;
            const quantity = parseFormattedNumber(item.quantity || 1);
            const discount = parseFormattedNumber(item.discount || 0);
            const surcharge = parseFormattedNumber(item.surcharge || 0);
            
            // Use item.sales_price if set (may have been modified in offer), otherwise use calculation total_amount
            const baseSalesPrice = parseFormattedNumber(item.sales_price || calc.total_amount || 0);
            let calcSales = baseSalesPrice * quantity;
            if (surcharge > 0) calcSales += (calcSales * surcharge) / 100;
            if (discount > 0) calcSales -= (calcSales * discount) / 100;
            totalSales += calcSales;

            // Extract hours from calculation
            const calcHours = extractHoursFromCalculation(calc);
            totalHours += calcHours.hours * quantity;
            totalHourlySales += calcHours.hourlySales * quantity;
            totalHourlyCost += calcHours.hourlyCost * quantity;

            // Calculate cost from calculation
            // Use item.purchase_price if set (may have been modified in offer), otherwise use calculation purchase_amount
            const baseCostPrice = parseFormattedNumber(item.purchase_price || calc.purchase_amount || 0);
            if (baseCostPrice > 0) {
              totalCost += baseCostPrice * quantity;
            } else if (calc.items && calc.items.length > 0) {
              // Fallback: calculate from items if purchase_amount is not available
              calc.items.forEach((calcItem) => {
                const costPrice = parseFormattedNumber(calcItem.purchase_price || 0);
                const calcQuantity = parseFormattedNumber(calcItem.quantity || 0);
                totalCost += costPrice * calcQuantity * quantity;
              });
            }
          } else {
            // Regular item
            const salesPrice = parseFormattedNumber(item.sales_price || 0);
            const costPrice = parseFormattedNumber(item.purchase_price || 0);
            const quantity = parseFormattedNumber(item.quantity || 0);
            const discount = parseFormattedNumber(item.discount || 0);
            const surcharge = parseFormattedNumber(item.surcharge || 0);
            
            let itemSales = salesPrice * quantity;
            if (surcharge > 0) itemSales += (itemSales * surcharge) / 100;
            if (discount > 0) itemSales -= (itemSales * discount) / 100;
            
            totalSales += itemSales;
            totalCost += costPrice * quantity;

            // Check for hours in regular items too
            if (item.hours && item.hour_rate) {
              const hours = parseFormattedNumber(item.hours || 0);
              const hourRate = parseFormattedNumber(item.hour_rate || 0);
              totalHours += hours * quantity;
              totalHourlySales += hours * hourRate * quantity;
            }
          }
        }
      });
    }

    // Calculate from calculations array (legacy support, if still used)
    if (calculations && calculations.length > 0) {
      calculations.forEach((calc) => {
        totalSales += parseFormattedNumber(calc.total_amount || 0);
        
        // Extract hours from calculation
        const calcHours = extractHoursFromCalculation(calc);
        totalHours += calcHours.hours;
        totalHourlySales += calcHours.hourlySales;
        totalHourlyCost += calcHours.hourlyCost;
        
        // Calculate cost from calculation items
        if (calc.items && calc.items.length > 0) {
          calc.items.forEach((item) => {
            const costPrice = parseFormattedNumber(item.purchase_price || 0);
            const quantity = parseFormattedNumber(item.quantity || 0);
            totalCost += costPrice * quantity;
          });
        }
      });
    }

    const margin = totalSales - totalCost;
    const marginPercent = totalSales > 0 ? (margin / totalSales) * 100 : 0;
    const hourMargin = totalHourlySales - totalHourlyCost;
    const hourMarginPercent = totalHourlySales > 0 ? (hourMargin / totalHourlySales) * 100 : 0;

    return {
      totalSales,
      totalCost,
      margin,
      marginPercent,
      totalHours,
      totalHourlySales,
      totalHourlyCost,
      hourMargin,
      hourMarginPercent,
    };
  };

  return (
    <>
      {isLoading ? (
        <ResourceLoading />
      ) : (
        <Formik
          initialValues={{
            id: offer?.id,
            customer_id: offer?.customer?.id,
            project_id: offer?.project?.id,
            department_id: offer?.department?.id,
            offer_number: offer?.offer_number,
            title: offer?.title || "",
            active: offer?.active,
            reference: offer?.reference,
            contact_id: offer?.contact?.id,
            our_contact_employee_id:
              offer?.our_contact_employee?.id || currentUser?.id,
            offer_date: offer?.offer_date || currentDate,
            delivery_date: offer?.delivery_date || currentDate,
            delivery_address: offer?.delivery_address,
            status: offer?.status,
            items: offer?.items || [],
            calculations: offer?.calculations || [],
            notes: offer?.notes || "",
            comment: offer?.comment,
            cover_page_active: offer?.cover_page_active || false,
            cover_page_title: offer?.cover_page_title || "",
            cover_page_description: offer?.cover_page_description || "",
            cover_page_image_id: offer?.cover_page_image_id || null,
          }}
          enableReinitialize={false}
          validationSchema={Yup.object({
            title: Yup.string().max(255),
            customer_id: Yup.string().required("Customer is required"),
          })}
          onSubmit={async (values, helpers) => {
            try {
              helpers.setSubmitting(true);
              const result = await updateOrCreateOffer(values);
              
              // Check if the action was rejected
              if (result.type?.endsWith('/rejected')) {
                const errors = result.payload?.response?.data?.errors || result.payload?.errors;
                const errorMessage = mapErrors(errors) || result.payload?.message || t("Noe gikk galt. Kunne ikke lagre data!");
                toast.error(errorMessage);
                helpers.setStatus({ success: false });
                helpers.setErrors({ submit: errorMessage });
                helpers.setSubmitting(false);
                return;
              }
              
              // Check if the action was fulfilled and has valid data
              if (result.type?.endsWith('/fulfilled')) {
                const payload = result.payload;
                
                // Check if payload indicates an error
                if (payload?.error || payload?.errors) {
                  const errorMessage = mapErrors(payload.errors) || payload.message || t("Noe gikk galt. Kunne ikke lagre data!");
                  toast.error(errorMessage);
                  helpers.setStatus({ success: false });
                  helpers.setErrors({ submit: errorMessage });
                  helpers.setSubmitting(false);
                  return;
                }
                
                // Success - update last saved hash and show success message
                // Don't refresh offer data to avoid resetting the form
                if (payload?.id) {
                  const valuesHash = JSON.stringify({
                    items: values.items,
                    calculations: values.calculations,
                    notes: values.notes,
                    customer_id: values.customer_id,
                    project_id: values.project_id,
                    offer_date: values.offer_date,
                    delivery_date: values.delivery_date,
                    reference: values.reference,
                    comment: values.comment,
                    active: values.active,
                  });
                  lastSavedRef.current = valuesHash;
                  toast.success(t("Data ble lagret!"));
                } else {
                  toast.success(t("Data ble lagret!"));
                }
                
                helpers.setStatus({ success: true });
                helpers.setSubmitting(false);
                return;
              }
              
              // Fallback: if result doesn't have expected structure, check payload directly
              if (result.payload) {
                const payload = result.payload;
                if (payload.error || payload.errors || payload.response?.status) {
                  const errorMessage = mapErrors(payload.errors) || payload.message || t("Noe gikk galt. Kunne ikke lagre data!");
                  toast.error(errorMessage);
                  helpers.setStatus({ success: false });
                  helpers.setErrors({ submit: errorMessage });
                  helpers.setSubmitting(false);
                  return;
                }
                
                // Success - update last saved hash and show success message
                // Don't refresh offer data to avoid resetting the form
                if (payload.id) {
                  const valuesHash = JSON.stringify({
                    items: values.items,
                    calculations: values.calculations,
                    notes: values.notes,
                    customer_id: values.customer_id,
                    project_id: values.project_id,
                    offer_date: values.offer_date,
                    delivery_date: values.delivery_date,
                    reference: values.reference,
                    comment: values.comment,
                    active: values.active,
                  });
                  lastSavedRef.current = valuesHash;
                  toast.success(t("Data ble lagret!"));
                } else {
                  toast.success(t("Data ble lagret!"));
                }
                
                helpers.setStatus({ success: true });
                helpers.setSubmitting(false);
                return;
              }
              
              // If we get here, something unexpected happened
              console.error("Unexpected result structure:", result);
              toast.error(t("Noe gikk galt. Kunne ikke lagre data!"));
              helpers.setStatus({ success: false });
              helpers.setSubmitting(false);
            } catch (err) {
              console.error("Error saving offer:", err);
              toast.error(t("Noe gikk galt. Kunne ikke lagre data!"));
              helpers.setStatus({ success: false });
              helpers.setErrors({ submit: err.message });
              helpers.setSubmitting(false);
            }
          }}
        >
          {(formik) => {
            // Store formik instance in ref for auto-save
            formikRef.current = formik;

            const margins = calculateMargins(formik.values.items, formik.values.calculations);

            return (
              <Form>
                {/* Header with Actions */}
                <div className="bg-white shadow-sm rounded-lg mb-6">
                  <div className="px-6 py-4 border-b border-gray-200">
                    <div className="flex items-center justify-between">
                      <div>
                        <h2 className="text-2xl font-bold text-gray-900">
                          {offer?.id ? offer?.displayTitle || `Tilbud #${offer?.id}` : "Nytt tilbud"}
                        </h2>
                      </div>
                      <div className="flex items-center gap-3">
                        <Button
                          variant="outlined"
                          startIcon={<PrinterIcon className="h-5 w-5" />}
                          onClick={async () => {
                            if (!formik.values.id) {
                              toast.error("Du må lagre tilbudet først");
                              return;
                            }
                            try {
                              const response = await axios.get(`/offers/${formik.values.id}/pdf_offer`);
                              if (response.data && response.data.pdf_url) {
                                window.open(response.data.pdf_url, '_blank');
                                toast.success("PDF generert!");
                              } else {
                                const errorMsg = response.data?.errors?.[0] || response.data?.error || "Kunne ikke generere PDF";
                                toast.error(errorMsg);
                              }
                            } catch (error) {
                              console.error("Error generating PDF:", error);
                              const errorMsg = error.response?.data?.errors?.[0] || error.response?.data?.error || error.message || "Kunne ikke generere PDF";
                              toast.error(errorMsg);
                            }
                          }}
                        >
                          Skriv ut
                        </Button>
                        <Button
                          variant="contained"
                          color="primary"
                          startIcon={<PaperAirplaneIcon className="h-5 w-5" />}
                          onClick={() => setSendDialogOpen(true)}
                          disabled={!formik.values.id}
                        >
                          Send til kunde
                        </Button>
                      </div>
                    </div>
                  </div>

                  {/* Tabs */}
                  <Tabs
                    value={activeTab}
                    onChange={(e, newValue) => setActiveTab(newValue)}
                    variant="scrollable"
                    scrollButtons="auto"
                    className="border-b border-gray-200"
                  >
                    <Tab
                      icon={<DocumentTextIcon className="h-5 w-5" />}
                      iconPosition="start"
                      label="Info"
                      id="offer-tab-0"
                      aria-controls="offer-tabpanel-0"
                    />
                    <Tab
                      icon={<DocumentIcon className="h-5 w-5" />}
                      iconPosition="start"
                      label="Notater"
                      id="offer-tab-1"
                      aria-controls="offer-tabpanel-1"
                    />
                    <Tab
                      icon={<ClockIcon className="h-5 w-5" />}
                      iconPosition="start"
                      label="Historikk"
                      id="offer-tab-2"
                      aria-controls="offer-tabpanel-2"
                    />
                    <Tab
                      icon={<PhotoIcon className="h-5 w-5" />}
                      iconPosition="start"
                      label="Forside"
                      id="offer-tab-3"
                      aria-controls="offer-tabpanel-3"
                    />
                  </Tabs>
                </div>

                {/* Tab Panels */}
                <TabPanel value={activeTab} index={0}>
                  {/* Info Tab */}
                  <div className="bg-white shadow-sm rounded-lg">
                    <div className="px-6 py-4 border-b border-gray-200">
                      <h3 className="text-lg font-semibold text-gray-900">
                        {t("order.info")}
                      </h3>
                    </div>
                    <div className="px-6 py-6">
                      <div className="grid grid-cols-6 gap-6">
                        <div className="col-span-6 sm:col-span-6">
                          <div className="flex flex-wrap items-center gap-3 rounded-lg border border-dashed border-gray-300 bg-gray-50 px-4 py-3">
                            <div className="min-w-0 flex-1">
                              <p className="text-sm font-medium text-gray-900">
                                Tilbudsdetaljer
                              </p>
                              <p className="text-sm text-gray-600">
                                Hold skjemaet kort. Legg kun til tittel, tilbudsnummer eller dato når det er nødvendig.
                              </p>
                            </div>
                            <Button
                              type="button"
                              variant="outlined"
                              size="small"
                              onClick={() => setTitleFieldOpen(true)}
                            >
                              {formik.values.title ? "Rediger tittel" : "Legg til tittel"}
                            </Button>
                            <Button
                              type="button"
                              variant="outlined"
                              size="small"
                              onClick={() => setOfferMetaFieldsOpen(true)}
                            >
                              {formik.values.offer_number || formik.values.offer_date
                                ? "Rediger tilbudsnummer og dato"
                                : "Legg til tilbudsnummer og dato"}
                            </Button>
                          </div>
                          <div className="mt-3 grid grid-cols-1 gap-2 sm:grid-cols-3">
                            {[
                              { label: "Tittel", value: formik.values.title, empty: "Ikke satt" },
                              { label: "Tilbudsnummer", value: formik.values.offer_number, empty: "Ikke tildelt" },
                              { label: "Tilbudsdato", value: formik.values.offer_date, empty: "Ikke satt" },
                            ].map((item) => (
                              <div
                                key={item.label}
                                className="flex min-w-0 items-center justify-between gap-2 rounded-md border border-gray-200 bg-white px-3 py-2"
                              >
                                <div className="min-w-0">
                                  <div className="text-[11px] font-medium uppercase tracking-wide text-gray-500">
                                    {item.label}
                                  </div>
                                  <div className="truncate text-sm font-medium text-gray-900">
                                    {item.value || item.empty}
                                  </div>
                                </div>
                                <button
                                  type="button"
                                  className="inline-flex h-8 w-8 flex-shrink-0 items-center justify-center rounded-md text-gray-500 hover:bg-gray-100 hover:text-gray-900 disabled:cursor-not-allowed disabled:opacity-40"
                                  onClick={() => copyValue(item.label, item.value)}
                                  disabled={!item.value}
                                  title={`Kopier ${item.label.toLowerCase()}`}
                                >
                                  <ClipboardDocumentIcon className="h-4 w-4" />
                                </button>
                              </div>
                            ))}
                          </div>
                        </div>

                        {titleFieldOpen && (
                          <div className="col-span-6 rounded-lg border border-gray-200 bg-white p-4">
                            <div className="mb-3 flex items-center justify-between gap-3">
                              <div>
                                <h4 className="text-sm font-semibold text-gray-900">
                                  Tittel
                                </h4>
                                <p className="text-xs text-gray-500">
                                  Valgfritt felt for tilbud som trenger en tydelig overskrift.
                                </p>
                              </div>
                              <Button
                                type="button"
                                variant="text"
                                size="small"
                                onClick={() => setTitleFieldOpen(false)}
                              >
                                Skjul
                              </Button>
                            </div>
                            <PMField
                              name="title"
                              type="text"
                              label="Tittel"
                              {...formik}
                            />
                          </div>
                        )}

                        {offerMetaFieldsOpen && (
                          <div className="col-span-6 rounded-lg border border-gray-200 bg-white p-4">
                            <div className="mb-3 flex items-center justify-between gap-3">
                              <div>
                                <h4 className="text-sm font-semibold text-gray-900">
                                  Tilbudsnummer og dato
                                </h4>
                                <p className="text-xs text-gray-500">
                                  Promang foreslår dette automatisk, så feltene trenger bare åpnes ved behov.
                                </p>
                              </div>
                              <Button
                                type="button"
                                variant="text"
                                size="small"
                                onClick={() => setOfferMetaFieldsOpen(false)}
                              >
                                Skjul
                              </Button>
                            </div>
                            <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                              {!formik.values.offer_number ? (
                                <PMField
                                  name="optionalOfferNumber"
                                  type="checkbox"
                                  label={t("Optional Offer Number")}
                                  onChange={() =>
                                    setOptionalOrderNumber(!optionalOrderNumber)
                                  }
                                  {...formik}
                                />
                              ) : null}
                              <PMField
                                disabled={optionalOrderNumber}
                                name="offer_number"
                                type="text"
                                label={t("offer_number")}
                                {...formik}
                              />
                              <PMField
                                name="offer_date"
                                type="date"
                                label="Tilbudsdato"
                                {...formik}
                              />
                            </div>
                          </div>
                        )}

                        <div className="col-span-6 sm:col-span-6 lg:col-span-2">
                          <PMField
                            name="reference"
                            type="text"
                            label={t("reference")}
                            {...formik}
                          />
                        </div>

                        <div className="col-span-6 sm:col-span-2">
                          <CustomerSelector
                            label="Kunde"
                            selected={
                              offer?.customer?.id ? offer?.customer : null
                            }
                            {...formik}
                          />
                        </div>

                        <div className="col-span-6 sm:col-span-2">
                          <ContactsSelector
                            selected={offer?.contact?.id ? offer?.contact : null}
                            module_param={offer?.customer?.id}
                            label="Kundekontakt"
                            name="contact_id"
                            {...formik}
                          />
                        </div>

                        <div className="col-span-6 sm:col-span-3 lg:col-span-2">
                          <ProjectSelector
                            label="Prosjekt"
                            selected={offer?.project?.id ? offer?.project : null}
                            onChange={(data) => handleProjectChange(data, formik)}
                            {...formik}
                          />
                        </div>

                        <div className="col-span-6 sm:col-span-3 lg:col-span-2">
                          <EmployeeSelector
                            name="our_contact_employee_id"
                            label="Vår kontaktperson"
                            selected={
                              offer?.our_contact_employee?.id
                                ? offer?.our_contact_employee
                                : currentUser
                            }
                            {...formik}
                          />
                        </div>

                        <div className="col-span-6 sm:col-span-3 lg:col-span-2">
                          <DepartmentSelector
                            label="Avdeling"
                            selected={selectedDepartment}
                            {...formik}
                          />
                        </div>

                        <div className="col-span-6">
                          <PMField
                            name="comment"
                            type="text"
                            label="Kommentar"
                            multiline
                            rows={2}
                            {...formik}
                          />
                        </div>
                      </div>

                      {/* Calculation Summary */}
                      <div className="mt-8 pt-6 border-t border-gray-200">
                        <div className="mb-4 flex items-center justify-between gap-3">
                          <div>
                            <h4 className="text-md font-semibold text-gray-900">
                              Summering
                            </h4>
                            <p className="text-sm text-gray-600">
                              Se nøkkeltall her. Åpne dialogen for detaljert beregning av timer, kostnad og margin.
                            </p>
                          </div>
                          <Button
                            type="button"
                            variant="outlined"
                            size="small"
                            onClick={() => openSummaryDialog("overview")}
                          >
                            Se hele summeringen
                          </Button>
                        </div>
                        <div className="grid grid-cols-1 gap-4 md:grid-cols-4">
                          <button
                            type="button"
                            className="rounded-lg border border-gray-200 bg-gray-50 p-4 text-left transition hover:border-blue-300 hover:bg-blue-50 focus:outline-none focus:ring-2 focus:ring-blue-500"
                            onClick={() => openSummaryDialog("sales")}
                          >
                            <div className="text-sm text-gray-600">Total salg</div>
                            <div className="mt-1 text-xl font-bold text-gray-900">
                              {priceFormatter.format(margins.totalSales)}
                            </div>
                            <div className="mt-2 text-sm text-blue-700">Vis salgsgrunnlag</div>
                          </button>
                          <button
                            type="button"
                            className="rounded-lg border border-gray-200 bg-gray-50 p-4 text-left transition hover:border-blue-300 hover:bg-blue-50 focus:outline-none focus:ring-2 focus:ring-blue-500"
                            onClick={() => openSummaryDialog("cost")}
                          >
                            <div className="text-sm text-gray-600">Total kostnad</div>
                            <div className="mt-1 text-xl font-bold text-gray-900">
                              {priceFormatter.format(margins.totalCost)}
                            </div>
                            <div className="mt-2 text-sm text-blue-700">Vis kostnadsgrunnlag</div>
                          </button>
                          <button
                            type="button"
                            className="rounded-lg border border-green-100 bg-green-50 p-4 text-left transition hover:border-green-300 focus:outline-none focus:ring-2 focus:ring-green-500"
                            onClick={() => openSummaryDialog("margin")}
                          >
                            <div className="text-sm text-gray-600">Margin</div>
                            <div className="mt-1 text-xl font-bold text-green-700">
                              {priceFormatter.format(margins.margin)} ({margins.marginPercent.toFixed(1)}%)
                            </div>
                            <div className="mt-2 text-sm text-green-700">Vis marginberegning</div>
                          </button>
                          <button
                            type="button"
                            className="rounded-lg border border-blue-100 bg-blue-50 p-4 text-left transition hover:border-blue-300 focus:outline-none focus:ring-2 focus:ring-blue-500"
                            onClick={() => openSummaryDialog("hours")}
                          >
                            <div className="text-sm text-gray-600">Timer</div>
                            <div className="mt-1 text-xl font-bold text-blue-700">
                              {margins.totalHours.toFixed(1)}t
                            </div>
                            <div className="mt-2 text-sm text-blue-700">Vis timeberegning</div>
                          </button>
                        </div>
                      </div>

                      <div className="mt-6">
                        <PMSwitch label="Aktiv" name="active" {...formik} />
                      </div>
                    </div>
                  </div>

                  {/* Items Section */}
                  <div className="bg-white shadow-sm rounded-lg mt-6">
                    <div className="px-6 py-4 border-b border-gray-200">
                      <h3 className="text-lg font-semibold text-gray-900">
                        Linjer
                      </h3>
                    </div>
                    <div className="px-6 py-6">
                      <OfferItems
                        group={true}
                        sales_price={true}
                        purchase_price={true}
                        discount={true}
                        surcharge={true}
                        {...formik}
                      />
                    </div>
                  </div>
                </TabPanel>

                <TabPanel value={activeTab} index={1}>
                  {/* Notes Tab */}
                  <OfferNotes
                    offer={offer}
                    formik={formik}
                    onAutoSave={() => autoSave(formik.values)}
                  />
                </TabPanel>

                <TabPanel value={activeTab} index={2}>
                  {/* History Tab */}
                  <OfferHistory offer={offer} />
                </TabPanel>

                <TabPanel value={activeTab} index={3}>
                  {/* Cover Page Tab */}
                  <OfferCoverPage
                    offer={offer}
                    formik={formik}
                    onAutoSave={() => autoSave(formik.values)}
                  />
                </TabPanel>

                <FormikSaveBar {...formik} />

                <PMDialog
                  open={summaryDialogOpen}
                  onClose={() => setSummaryDialogOpen(false)}
                  title={{
                    overview: "Tilbudssummering",
                    sales: "Salgsgrunnlag",
                    cost: "Kostnadsgrunnlag",
                    margin: "Marginberegning",
                    hours: "Timeberegning",
                  }[summaryDialogType]}
                  size="3xl"
                  closeOnOutsideClick
                >
                  <div className="space-y-6">
                    {summaryDialogType === "overview" && (
                      <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
                        <div className="rounded-lg bg-gray-50 p-4">
                          <div className="text-sm text-gray-600">Total salg</div>
                          <div className="text-2xl font-bold text-gray-900">
                            {priceFormatter.format(margins.totalSales)}
                          </div>
                        </div>
                        <div className="rounded-lg bg-gray-50 p-4">
                          <div className="text-sm text-gray-600">Total kostnad</div>
                          <div className="text-2xl font-bold text-gray-900">
                            {priceFormatter.format(margins.totalCost)}
                          </div>
                        </div>
                        <div className="rounded-lg bg-green-50 p-4">
                          <div className="text-sm text-gray-600">Margin</div>
                          <div className="text-2xl font-bold text-green-700">
                            {priceFormatter.format(margins.margin)} ({margins.marginPercent.toFixed(1)}%)
                          </div>
                        </div>
                        <div className="rounded-lg bg-blue-50 p-4">
                          <div className="text-sm text-gray-600">Timer</div>
                          <div className="text-2xl font-bold text-blue-700">
                            {margins.totalHours.toFixed(1)}t
                          </div>
                          {margins.totalHourlySales > 0 && (
                            <div className="mt-1 text-xs text-gray-500">
                              {priceFormatter.format(margins.totalHourlySales)}
                            </div>
                          )}
                        </div>
                      </div>
                    )}

                    {summaryDialogType === "sales" && (
                      <div className="rounded-lg border border-gray-200 bg-gray-50 p-4">
                        <div className="text-sm text-gray-600">Total salg</div>
                        <div className="mt-1 text-3xl font-bold text-gray-900">
                          {priceFormatter.format(margins.totalSales)}
                        </div>
                        <p className="mt-4 text-sm text-gray-700">
                          Salg beregnes fra tilbudslinjer og kalkyler med salgspris ganger antall. Linjer med påslag øker salgsgrunnlaget, mens rabatt trekkes fra.
                        </p>
                      </div>
                    )}

                    {summaryDialogType === "cost" && (
                      <div className="rounded-lg border border-gray-200 bg-gray-50 p-4">
                        <div className="text-sm text-gray-600">Total kostnad</div>
                        <div className="mt-1 text-3xl font-bold text-gray-900">
                          {priceFormatter.format(margins.totalCost)}
                        </div>
                        <p className="mt-4 text-sm text-gray-700">
                          Kostnad beregnes fra innkjøpspris på tilbudslinjer og kalkyler, ganget med antall. For kalkyler brukes kalkylens innkjøpssum når den finnes, ellers summeres kalkylelinjene.
                        </p>
                      </div>
                    )}

                    {summaryDialogType === "margin" && (
                      <div className="space-y-4">
                        <div className="rounded-lg bg-green-50 p-4">
                          <div className="text-sm text-gray-600">Margin</div>
                          <div className="mt-1 text-3xl font-bold text-green-700">
                            {priceFormatter.format(margins.margin)} ({margins.marginPercent.toFixed(1)}%)
                          </div>
                        </div>
                        <div className="grid grid-cols-1 gap-4 md:grid-cols-3">
                          <div className="rounded-lg border border-gray-200 p-4">
                            <div className="text-sm text-gray-600">Salg</div>
                            <div className="text-lg font-semibold text-gray-900">
                              {priceFormatter.format(margins.totalSales)}
                            </div>
                          </div>
                          <div className="rounded-lg border border-gray-200 p-4">
                            <div className="text-sm text-gray-600">Kostnad</div>
                            <div className="text-lg font-semibold text-gray-900">
                              {priceFormatter.format(margins.totalCost)}
                            </div>
                          </div>
                          <div className="rounded-lg border border-gray-200 p-4">
                            <div className="text-sm text-gray-600">Formel</div>
                            <div className="text-sm font-semibold text-gray-900">
                              Salg - kostnad = margin
                            </div>
                          </div>
                        </div>
                        <p className="text-sm text-gray-700">
                          Marginprosent beregnes som margin delt på totalt salg.
                        </p>
                      </div>
                    )}

                    {summaryDialogType === "hours" && margins.totalHours > 0 && (
                      <div className="rounded-lg border border-blue-200 bg-blue-50 p-4">
                        <h5 className="mb-3 text-sm font-semibold text-gray-900">
                          Timeoversikt
                        </h5>
                        <div className="grid grid-cols-1 gap-4 text-sm md:grid-cols-3">
                          <div>
                            <div className="text-gray-600">Total timer</div>
                            <div className="font-semibold text-gray-900">
                              {margins.totalHours.toFixed(2)}t
                            </div>
                          </div>
                          {margins.totalHourlySales > 0 && (
                            <div>
                              <div className="text-gray-600">Timeinntekt</div>
                              <div className="font-semibold text-gray-900">
                                {priceFormatter.format(margins.totalHourlySales)}
                              </div>
                              <div className="text-xs text-gray-500">
                                {priceFormatter.format(margins.totalHourlySales / margins.totalHours)}/t
                              </div>
                            </div>
                          )}
                          {margins.totalHourlyCost > 0 && (
                            <div>
                              <div className="text-gray-600">Timekostnad</div>
                              <div className="font-semibold text-gray-900">
                                {priceFormatter.format(margins.totalHourlyCost)}
                              </div>
                              <div className="text-xs text-gray-500">
                                {priceFormatter.format(margins.totalHourlyCost / margins.totalHours)}/t
                              </div>
                            </div>
                          )}
                          {margins.hourMargin !== undefined && margins.totalHourlySales > 0 && (
                            <div className="md:col-span-3">
                              <div className="text-gray-600">Time margin</div>
                              <div className={`font-semibold ${margins.hourMargin >= 0 ? "text-green-700" : "text-red-700"}`}>
                                {priceFormatter.format(margins.hourMargin)} ({margins.hourMarginPercent.toFixed(1)}%)
                              </div>
                            </div>
                          )}
                        </div>
                      </div>
                    )}

                    {summaryDialogType === "hours" && margins.totalHours <= 0 && (
                      <div className="rounded-lg border border-gray-200 bg-gray-50 p-4 text-sm text-gray-700">
                        Det er ikke registrert timer på tilbudslinjene eller kalkylene ennå.
                      </div>
                    )}
                  </div>
                </PMDialog>
              </Form>
            );
          }}
        </Formik>
      )}

      {/* Send Dialog */}
      <OfferSendDialog
        open={sendDialogOpen}
        onClose={() => setSendDialogOpen(false)}
        offer={offer}
        isLoading={isLoading}
      />
    </>
  );
};
